import { useState, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import type { RadioPlanAssignment, DynamicPlan } from '@/mocks/superAdminPlans';
import { getPlanById } from '@/mocks/superAdminPlans';

interface PlanUpgradeModalProps {
  radio: RadioPlanAssignment | null;
  planDefs: DynamicPlan[];
  onClose: () => void;
  onConfirm: (radio: RadioPlanAssignment, newPlanId: string) => void;
}

function formatStorage(gb: number): string {
  if (gb >= 1000) return `${(gb / 1024).toFixed(1)} TB`;
  return `${gb} GB`;
}

function swal() {
  return Swal.mixin({
    customClass: {
      popup: 'rounded-2xl',
      title: 'font-bold text-sm',
      confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      htmlContainer: 'text-sm',
    },
    buttonsStyling: false,
  });
}

export default function PlanUpgradeModal({ radio, planDefs, onClose, onConfirm }: PlanUpgradeModalProps) {
  const [selectedId, setSelectedId] = useState<string>(radio?.planId ?? '');
  const [includeAnnual, setIncludeAnnual] = useState(false);
  const [step, setStep] = useState<'select' | 'confirm'>('select');

  const currentPlan = getPlanById(radio?.planId ?? '');
  const selectedPlan = getPlanById(selectedId);
  const activePlans = planDefs.filter((p) => p.active);

  const isUpgrade = selectedPlan && currentPlan && selectedPlan.priceMonthly > currentPlan.priceMonthly;
  const isDowngrade = selectedPlan && currentPlan && selectedPlan.priceMonthly < currentPlan.priceMonthly;
  const isSamePlan = selectedId === radio?.planId;

  const downgradeBlocked = useMemo(() => {
    if (!radio || !selectedPlan || !isDowngrade) return false;
    return radio.storageUsedGB > selectedPlan.storageGB;
  }, [radio, selectedPlan, isDowngrade]);

  const monthlyPrice = selectedPlan?.priceMonthly ?? 0;
  const annualPrice = Math.round(monthlyPrice * 10);
  const totalToday = includeAnnual ? annualPrice : monthlyPrice;
  const nextRenewal = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  const storageDiff = useMemo(() => {
    if (!radio || !selectedPlan) return '';
    const diff = selectedPlan.storageGB - radio.storageTotalGB;
    if (diff > 0) return `+${formatStorage(diff)}`;
    if (diff < 0) return `-${formatStorage(Math.abs(diff))}`;
    return 'Sin cambios';
  }, [radio, selectedPlan]);

  const storagePct = radio && radio.storageTotalGB > 0 ? (radio.storageUsedGB / radio.storageTotalGB) * 100 : 0;

  const handleSelectPlan = useCallback((plan: DynamicPlan) => {
    if (!radio) return;
    if (plan.id === radio.planId) {
      swal().fire({
        title: 'Este es el plan actual',
        text: `${radio.radioName} ya está en ${plan.name}.`,
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#6b7280',
      });
      return;
    }
    setSelectedId(plan.id);
    setIncludeAnnual(false);
  }, [radio]);

  const handleContinue = useCallback(() => {
    if (!radio || !selectedPlan) return;

    if (isDowngrade && downgradeBlocked) {
      swal().fire({
        title: 'Downgrade bloqueado',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:8px"><strong>${radio.radioName}</strong> está usando <strong>${radio.storageUsedGB.toFixed(1)} GB</strong> de almacenamiento.</p>
          <p style="margin-bottom:8px">El plan <strong>${selectedPlan.name}</strong> solo incluye <strong>${selectedPlan.storageGB} GB</strong>.</p>
          <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:10px">
            <p style="color:#dc2626;font-weight:600;margin:0;font-size:12px"><i class="ri-alert-line"></i> No se puede bajar de plan</p>
            <p style="color:#dc2626;margin:4px 0 0;font-size:11px">El almacenamiento actual (${radio.storageUsedGB.toFixed(1)} GB) excede el límite del nuevo plan (${selectedPlan.storageGB} GB).</p>
            <p style="color:#9ca3af;margin:4px 0 0;font-size:11px">Liberá espacio eliminando archivos o elegí un plan con suficiente storage.</p>
          </div>
        </div>`,
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    if (isDowngrade) {
      const s = swal();
      s.fire({
        title: '¿Bajar de plan?',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:8px"><strong>${radio.radioName}</strong> pasará de <strong>${currentPlan?.name}</strong> a <strong>${selectedPlan.name}</strong>.</p>
          <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px;margin-bottom:8px">
            <p style="font-weight:600;color:#92400e;margin:0 0 4px;font-size:11px"><i class="ri-alert-line"></i> Importante</p>
            <p style="color:#92400e;margin:0;font-size:11px">Al bajar de plan, la radio podría perder acceso a funciones y almacenamiento. El downgrade se aplica en la próxima renovación.</p>
          </div>
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, bajar de plan',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) setStep('confirm');
      });
      return;
    }

    setStep('confirm');
  }, [radio, selectedPlan, isDowngrade, downgradeBlocked, currentPlan]);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!radio || !selectedPlan || !selectedId) return;
    const s = swal();

    await s.fire({
      title: 'Generando invoice...',
      html: '<p style="font-size:13px;color:#6b7280">Preparando el cargo para el nuevo plan.</p>',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading(); },
    });
    await new Promise((r) => setTimeout(r, 1500));
    Swal.close();

    const invoiceId = `INV-${Date.now().toString(36).toUpperCase()}`;
    const label = includeAnnual ? `$${annualPrice.toFixed(2)} (licencia anual)` : monthlyPrice === 0 ? 'Gratis' : `$${monthlyPrice.toFixed(2)} (mensual)`;

    const result = await s.fire({
      title: 'Invoice generada',
      html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
        <p style="margin-bottom:10px">Completá el pago para activar el nuevo plan de <strong>${radio.radioName}</strong>.</p>
        <div style="background:#f3f4f6;border-radius:8px;padding:12px;margin-bottom:10px">
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Radio:</span><span style="font-weight:600">${radio.radioName} · ${radio.frequency}</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Plan actual:</span><span style="font-weight:600">${currentPlan?.name} ($${currentPlan?.priceMonthly}/mes)</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Nuevo plan:</span><span style="font-weight:600;color:#f59e0b">${selectedPlan.name} ($${selectedPlan.priceMonthly}/mes)</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <span style="color:#6b7280">Invoice:</span><span style="font-weight:600;font-family:monospace">${invoiceId}</span>
          </div>
          <div style="border-top:1px solid #e5e7eb;margin-top:8px;padding-top:8px;display:flex;justify-content:space-between">
            <span style="font-weight:600">Total:</span><span style="font-weight:700;font-size:15px">${label}</span>
          </div>
        </div>
        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px;font-size:11px;color:#92400e">
          <i class="ri-information-line"></i> <strong>Upgrade pendiente de pago.</strong> El nuevo plan se activará cuando confirmes el pago.
        </div>
      </div>`,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Pagar ahora (mock)',
      cancelButtonText: 'Ir a Billing',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (result.isConfirmed) {
      await s.fire({
        title: 'Procesando pago...',
        html: '<p style="font-size:13px;color:#6b7280">Verificando transacción...</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => { Swal.showLoading(); },
      });
      await new Promise((r) => setTimeout(r, 2000));
      Swal.close();

      onConfirm(radio, selectedId);

      await s.fire({
        title: '¡Plan activado!',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:10px"><strong>${radio.radioName}</strong> ahora está en <strong style="color:#f59e0b">${selectedPlan.name}</strong>.</p>
          <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px">
            <p style="font-weight:600;color:#166534;margin:0 0 4px;font-size:11px"><i class="ri-check-line"></i> Cambios aplicados</p>
            <ul style="list-style:none;padding:0;margin:0;font-size:11px;color:#166534">
              <li style="padding:1px 0"><i class="ri-check-line"></i> Storage: ${storageDiff}</li>
              <li style="padding:1px 0"><i class="ri-check-line"></i> Próx. renovación: ${nextRenewal}</li>
              <li style="padding:1px 0"><i class="ri-check-line"></i> Plan desbloqueado</li>
            </ul>
          </div>
        </div>`,
        icon: 'success',
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#10b981',
      });
    }

    onClose();
  }, [radio, selectedPlan, selectedId, includeAnnual, monthlyPrice, annualPrice, storageDiff, nextRenewal, currentPlan, onConfirm, onClose]);

  const handleBack = useCallback(() => setStep('select'), []);

  if (!radio) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="p-5 border-b border-background-200 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">
                {step === 'select' ? 'Cambiar plan' : 'Confirmar cambio'}
              </h3>
              <p className="text-foreground-400 text-xs mt-0.5">
                {radio.radioName} · {radio.frequency} · Actual: {currentPlan?.name}
              </p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          {step === 'select' && (
            <>
              {/* Storage usage */}
              <div className="bg-background-100 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Almacenamiento actual</span>
                  <span className="text-foreground-400 text-[9px]">{(storagePct ?? 0).toFixed(1)}% usado</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-foreground-800 text-xs font-mono font-semibold">{radio.storageUsedGB.toFixed(1)} GB</span>
                  <span className="text-foreground-300 text-[10px]">de {radio.storageTotalGB} GB</span>
                </div>
                <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${(storagePct ?? 0) >= 90 ? 'bg-red-500' : (storagePct ?? 0) >= 70 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(storagePct ?? 0, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Plan selector */}
              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-2">Nuevo plan</label>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {activePlans.map((def) => {
                    const isCurrent = def.id === radio.planId;
                    const isSelected = def.id === selectedId;
                    return (
                      <button
                        key={def.id}
                        onClick={() => handleSelectPlan(def)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                          isSelected
                            ? 'border-foreground-300 bg-background-100'
                            : 'border-background-200 bg-background-50 hover:border-background-300'
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${def.color}14` }}
                        >
                          <span className="text-sm font-bold" style={{ color: def.color }}>{def.name.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-bold text-foreground-800">{def.name}</p>
                            {isCurrent && (
                              <span className="px-1.5 py-0.5 rounded-full bg-background-200 text-foreground-400 text-[9px]">Actual</span>
                            )}
                          </div>
                          <p className="text-foreground-400 text-[10px]">
                            {formatStorage(def.storageGB)} · {def.priceMonthly === 0 ? 'Gratis' : `$${def.priceMonthly}/mes`}
                            {def.trialDays > 0 && def.priceMonthly > 0 && (
                              <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-accent-50 text-accent-600 text-[9px] font-semibold">
                                <i className="ri-timer-line text-[8px]"></i>
                                {def.trialDays}d
                              </span>
                            )}
                          </p>
                        </div>
                        {isSelected && (
                          <i className="ri-checkbox-circle-fill text-foreground-800 text-base"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Summary */}
              {!isSamePlan && selectedPlan && currentPlan && (
                <div className={`rounded-xl p-4 border-2 ${isUpgrade ? 'border-emerald-300 bg-emerald-50/60' : isDowngrade ? 'border-amber-300 bg-amber-50/60' : 'border-background-200 bg-background-50'}`}>
                  <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-3">Resumen del cambio</p>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-foreground-400">Plan actual</p>
                      <p className="text-foreground-800 font-bold">{currentPlan.name} (${currentPlan.priceMonthly}/mes)</p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Nuevo plan</p>
                      <p className={`font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : 'text-foreground-800'}`}>
                        {selectedPlan.name} (${selectedPlan.priceMonthly}/mes)
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Storage</p>
                      <p className={`font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : 'text-foreground-800'}`}>
                        {radio.storageTotalGB} GB → {selectedPlan.storageGB} GB ({storageDiff})
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Próx. renovación</p>
                      <p className="text-foreground-800 font-bold">{nextRenewal}</p>
                    </div>
                  </div>

                  {selectedPlan.priceMonthly > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeAnnual}
                          onChange={(e) => setIncludeAnnual(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-background-300 peer-checked:bg-accent-500 rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                        <div>
                          <p className="text-foreground-800 text-xs font-semibold">Licencia anual (10 meses)</p>
                          <p className="text-foreground-400 text-[10px]">Ahorrá ~17% — ${annualPrice.toFixed(2)} en vez de ${(selectedPlan.priceMonthly * 12).toFixed(2)}</p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === 'confirm' && selectedPlan && currentPlan && (
            <>
              <button onClick={handleBack} className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line text-xs"></i>Volver a elegir plan
              </button>

              <div className="bg-background-100 rounded-xl border border-background-200 p-5">
                <h4 className="text-foreground-950 font-bold text-sm mb-4">
                  <i className="ri-file-list-3-line text-amber-600 mr-2"></i>
                  Resumen del cambio de plan
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-background-50 rounded-xl p-4 border border-background-200">
                      <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-1">Plan actual</p>
                      <p className="text-foreground-800 text-sm font-bold">{currentPlan.name}</p>
                      <p className="text-foreground-400 text-[10px]">${currentPlan.priceMonthly}/mes · {radio.storageTotalGB} GB</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <p className="text-amber-600 text-[10px] font-semibold uppercase tracking-wide mb-1">
                        {isUpgrade ? 'Nuevo plan' : 'Downgrade'}
                      </p>
                      <p className="text-foreground-800 text-sm font-bold">{selectedPlan.name}</p>
                      <p className="text-foreground-400 text-[10px]">${selectedPlan.priceMonthly}/mes · {selectedPlan.storageGB} GB</p>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-xl p-4 border border-background-200 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">{includeAnnual ? 'Licencia anual' : 'Cargo inmediato'}</span>
                      <span className="text-foreground-800 font-bold">
                        {monthlyPrice === 0 ? 'Gratis' : includeAnnual ? `$${annualPrice.toFixed(2)}` : `$${monthlyPrice.toFixed(2)}`}
                      </span>
                    </div>
                    {monthlyPrice > 0 && !includeAnnual && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-400">Licencia anual (opcional)</span>
                        <span className="text-foreground-500">${annualPrice.toFixed(2)}/año</span>
                      </div>
                    )}
                    <div className="border-t border-background-200 pt-2 flex items-center justify-between">
                      <span className="text-foreground-800 text-sm font-bold">Total hoy</span>
                      <span className="text-foreground-950 text-lg font-bold">
                        {totalToday === 0 ? 'Gratis' : `$${totalToday.toFixed(2)}`}
                      </span>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-xl p-4 border border-background-200 space-y-2">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Qué cambia</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">Storage</span>
                      <span className={`font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : 'text-foreground-600'}`}>{storageDiff}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">Próx. renovación</span>
                      <span className="text-foreground-600 font-medium">{nextRenewal}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 p-5 border-t border-background-200 bg-background-50">
          {step === 'select' ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleContinue}
                disabled={isSamePlan}
                className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  !isSamePlan && selectedPlan
                    ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                    : 'bg-background-200 text-foreground-300 cursor-not-allowed'
                }`}
              >
                {isSamePlan ? 'Es tu plan actual' : isUpgrade ? 'Continuar upgrade' : isDowngrade ? 'Revisar downgrade' : 'Continuar cambio'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs mr-1.5"></i>Volver
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-xs font-medium text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmUpgrade}
                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-foreground-950 text-background-50 hover:bg-foreground-800 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line text-sm mr-1.5"></i>
                Confirmar y generar invoice
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}